import type { JSXElementConstructor } from "react";
import EventEmitter from "event-lite";
import i18n from "i18next";
import type { Thing } from "schema-dts";

import {
  EVENT_APP_RENDER,
  EVENT_RENDER_ROUTE_COMPONENT,
} from "@/__main__/App.jsx";
import {
  MAX_RECENT_PATHS,
  recentlyPersistedPaths,
} from "@/__main__/app-state.mjs";
import {
  IS_APP,
  IS_DEV,
  IS_NODE,
  IS_NODE_BROWSERLESS,
} from "@/__main__/constants.mjs";
import { featurePromises } from "@/__main__/feature-flags.mjs";
import blitzMessage, { EVENTS } from "@/__main__/ipc-core.mjs";
import mainRefs from "@/__main__/refs.mjs";
import eventBus from "@/app/app-event-bus.mjs";
import {
  appURLs,
  hardCodeURLs,
  JS_FILE_EXTENSION,
  LOCALES,
} from "@/app/constants.mjs";
import { EVENT_ERROR } from "@/app/ErrorBoundary.jsx";
import MissingComponent from "@/app/Missing.jsx";
import i18nInit from "@/i18n/i18n.mjs";
import { catchAllRoutes } from "@/routes/constants.mjs";
import type { BaseRoute } from "@/routes/routes.mjs";
import { hasMarkup } from "@/util/constants.mjs";
import {
  ModuleImportError,
  NotFoundError,
  RedirectError,
} from "@/util/custom-errors.mjs";
import deepEqual from "@/util/deep-equal.mjs";
import { devDebug, devError, devWarn } from "@/util/dev.mjs";
import dynamicImport from "@/util/dynamic-import.mjs";
import sourceMap from "@/util/error-sourcemap.mjs";
import globals from "@/util/global-whitelist.mjs";
import hookFetch from "@/util/hook-fetch.mjs";
import htmlEscape from "@/util/html-escape.mjs";
import importBasePath from "@/util/import-base-path.mjs";
import InitialComponent from "@/util/InitialComponent.jsx";
import { normalizePathname, rewritePathname } from "@/util/mod-pathname.mjs";
import type { RouteRegExp } from "@/util/path-regexp.mjs";
import { stringifyPath } from "@/util/path-regexp.mjs";
import scrollOnRouteChange from "@/util/scroll-route.mjs";
import {
  CONFIG,
  emitter as stateEmitter,
  EVENT_ADD_KEY,
  MAX_LENGTH,
} from "@/util/state-cleanup.mjs";
import symbolName from "@/util/symbol-name.mjs";

export { MissingComponent, NotFoundError };

// things to test:
// - guaranteed ordering of route transitions (no race conditions on loading)
// - events are emitted and handled

const isHashRouting = Boolean(globals.location?.hash);

// There are certain cases where we wanna prevent automated transitions.
let isTransitionAllowed = true;

export const setTransitionAllowed = (value) => (isTransitionAllowed = value);

export const EVENT_CHANGE_ROUTE = "EVENT_CHANGE_ROUTE" as const;
export const EVENT_CHANGE_ROUTE_COMPONENT =
  "EVENT_CHANGE_ROUTE_COMPONENT" as const;
export const EVENT_LOADING_DATA = "EVENT_LOADING_DATA" as const;
export const EVENT_LOADED_DATA = "EVENT_LOADED_DATA" as const;
export const EVENT_LOADED_META = "EVENT_LOADED_META" as const;

export const pathsWritten = [];

function defaultMeta(): Meta {
  return {
    title: ["common:blitz", "Blitz"],
    description: ["home:downloadLanding.description", "Play smart."],
  };
}

function missingMeta(): Meta {
  return {
    title: ["common:missing", "Page missing"],
    description: ["common:error.generalAppError.title", "Oops"],
  };
}

// This is an array of in-flight route transitions, used mainly to queue up
// and await previous ones.
const __routeTransitions = [];

export const componentCache: Map<unknown, ComponentModule> = new Map();

export function getModulePath(p) {
  return `${importBasePath(import.meta.url)}${p.replace(
    /\.(m(j|t)s|(j|t)sx|mdx?)$/,
    JS_FILE_EXTENSION,
  )}`;
}

const WrappedComponentMap = new WeakMap();

// A dynamically imported module. a page should have a default component and a meta fn
export interface ComponentModule {
  default: () => JSX.Element;
  meta: () => Meta;
}

export type RouteComponent = (() => () => JSX.Element) & {
  // DEPRECATED: we are doing away with hub route shiz
  hubRoute?: () => HubRouteMeta;
};

// Page meta, title shows as the tab name. description for SEO
export interface Meta {
  title: Translation;
  description: Translation;
  subtitle?: boolean | Translation;

  image?: MediaMeta;

  /**
   * Indicates the canonical URL of the page. This is used for duplicate content detection.
   */
  canonicalUrl?: string;

  /**
   * Follow Structured Data guidelines and policies here: https://developers.google.com/search/docs/appearance/structured-data/sd-policies
   */
  schema?: Schema | Schema[];

  linkTags?: LinkTag[];
}

export type Schema = Exclude<Thing, string>;

interface MediaMeta {
  url: string;
  alt: Translation;

  width?: number;
  height?: number;
}

// DEPRECATED: we are doing away with hub route shiz
export interface HubRouteMeta {
  title: Translation | JSXElementConstructor<unknown>;
  description: Translation;

  subRoute?: string;
  Header?: () => JSX.Element;
}

interface RouteComponentSettings {
  showPageHeader?: boolean;
  showAppNavigation?: boolean;
  fullBleed?: boolean;
}

// This router is a NETGEAR Nighthawk RAXE500
export interface Router {
  error: Error;
  isLoaded: boolean;
  isBackwards: boolean;
  route: ActiveRoute | null;
  previousRoute: ActiveRoute | null;
  RouteComponent: RouteComponent & RouteComponentSettings;
  meta: () => Meta;
  events: EventEmitter & {
    EVENT_CHANGE_ROUTE: typeof EVENT_CHANGE_ROUTE;
    EVENT_CHANGE_ROUTE_COMPONENT: typeof EVENT_CHANGE_ROUTE_COMPONENT;
    EVENT_LOADING_DATA: typeof EVENT_LOADING_DATA;
    EVENT_LOADED_DATA: typeof EVENT_LOADED_DATA;
  };
  lastEvent: EventType;
  refetchData: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
  lastPathname: string;
}

export const ROUTE_INITIATED_BY_USER = symbolName("route-initiated-by-user");
export const ROUTE_INITIATED_BY_AUTO = symbolName("route-initiated-by-auto");

// Extra info for being on the current route
export interface ActiveRoute<
  S extends Record<PrimitiveKey, unknown> = Record<PrimitiveKey, unknown>,
> extends BaseRoute {
  path: string | RouteRegExp;
  currentPath: string;
  initiatedBy?: typeof ROUTE_INITIATED_BY_USER | typeof ROUTE_INITIATED_BY_AUTO;
  state: RouteState<S>;
  searchParams: URLSearchParams;
  parameters: string[];
  metadata?: Meta;
}

export interface RouteStateExt {
  isInitializing?: boolean;
  isRedirect?: boolean;
  isProgrammatic?: boolean;
  isUpdate?: boolean;
  isManual?: boolean;
  transient?: Record<string, unknown>;
}

// Extensible page/route state
export type RouteState<
  S extends Record<PrimitiveKey, unknown> = Record<PrimitiveKey, unknown>,
> = S & RouteStateExt;

type EventType = string; // from event-lite EventEmitter

// anything that can be turned into URLSearchParams with its ctor
type URLSearchParamsInit =
  | string
  | URLSearchParams
  | string[][]
  | Record<string, string>;

const INITIAL_ROUTE_COMPONENT = () => InitialComponent;

const router: Router = {
  error: null,
  isLoaded: false,
  isBackwards: false,
  canGoBack: false,
  canGoForward: false,
  route: null,
  previousRoute: null,
  RouteComponent: INITIAL_ROUTE_COMPONENT,
  meta: defaultMeta,
  events: Object.assign(new EventEmitter(), {
    EVENT_CHANGE_ROUTE,
    EVENT_CHANGE_ROUTE_COMPONENT,
    EVENT_LOADING_DATA,
    EVENT_LOADED_DATA,
  }),
  lastEvent: null,
  lastPathname: "/",
  refetchData() {},
};

function emitEvent(evt: EventType, ...rest) {
  router.lastEvent = evt;
  router.events.emit(evt, ...rest);
}

export function listenRouterEvents(rootNode = globals.document) {
  globals.addEventListener("popstate", onPopstate);
  if (isHashRouting) globals.addEventListener("hashchange", onPopstate);
  rootNode?.addEventListener("click", clickHandler);
}

export function unlistenRouterEvents(rootNode = globals.document) {
  globals.removeEventListener("popstate", onPopstate);
  if (isHashRouting) globals.removeEventListener("hashchange", onPopstate);
  rootNode?.removeEventListener("click", clickHandler);
}

// navigation direction logic
// use sessionStorage to persist across page reloads (exact lifetime of page history)
const initialHistoryUid =
  Number.parseInt(globals.sessionStorage?.getItem("__history_uid"), 10) || 0;
let historyHead =
  Number.parseInt(globals.sessionStorage?.getItem("__history_head"), 10) ||
  initialHistoryUid;
let historyUid = initialHistoryUid;

const assignHistoryUid = (uid = historyUid + 1, shouldIgnoreHead = false) => {
  if (uid >= Number.MAX_SAFE_INTEGER) uid = 0; // can only cmp ge safely!!
  router.isBackwards = uid < historyUid;
  historyUid = uid;
  if (!shouldIgnoreHead) historyHead = historyUid;
  router.canGoBack = historyUid > 0;
  router.canGoForward = historyUid < historyHead;
  globals.sessionStorage?.setItem("__history_uid", historyUid.toString(10));
  globals.sessionStorage?.setItem("__history_head", historyHead.toString(10));
  return uid;
};

async function onPopstate(e: PopStateEvent) {
  const state: RouteState = e.state || {};
  // isBackwards logic
  const uid = e.state?.__history_uid || 0;

  if (uid === historyUid) return;

  assignHistoryUid(uid, true);

  // navigate to the new route
  const {
    location: { pathname, search, hash },
  } = globals;

  try {
    state.isManual = true;

    await __setRoute(isHashRouting ? hash.slice(1) : pathname, search, state);
  } catch (error) {
    devError("AUTOMATED SET ROUTE FAILED", error);
  }
}

let isInteractive = false;

eventBus.once(EVENT_APP_RENDER, () => {
  isInteractive = true;
});

function clickHandler(event: MouseEvent) {
  // If we haven't yet completed hydration, we need to let the browser navigate normally
  // so that a page that's hydrating slowly can still be navigated.
  if (!isInteractive) {
    return;
  }

  if (event.defaultPrevented) return;

  const { target } = event;
  if (!(target instanceof Element)) return; // safe type narrow
  const a = target.closest("a");
  if (!a) return;
  const { href, dataset, target: aTarget } = a;

  if (a.hasAttribute("download")) {
    // don't handle download links
    globals.open(href, "_self");
    return;
  }

  if (!href) {
    devError(`A link with an empty href attribute was clicked!`);
    event.preventDefault();
    return;
  }

  let url;
  try {
    url = new URL(href);
  } catch (_e) {
    throw new Error(`Failed to construct URL for "${href}"`);
  }
  const { origin, pathname, search } = url;

  // differentiate app links from external links
  if (origin !== globals.location.origin || aTarget || dataset?.exempt) {
    if (IS_APP) {
      // External links need to be opened in a a special way, since
      // we're in a standalone app.
      blitzMessage(EVENTS.OPEN_EXTERNAL, url.toString());

      event.preventDefault();
    }

    return;
  }

  // let the browser take the wheel for these special cliccs
  if ((event.ctrlKey || event.metaKey) && !IS_APP) {
    return;
  }

  event.preventDefault();

  // ignore if same route
  if (url.href === globals.location.href) return;

  const state: RouteState = { __history_uid: assignHistoryUid() };
  globals.history.pushState(
    state,
    "",
    `${isHashRouting ? "#" : ""}${pathname}${search}`,
  );

  (async () => {
    try {
      state.isManual = true;

      await __setRoute(pathname, search, state);
    } catch (error) {
      devError("USER-INITIATED SET ROUTE FAILED", error);
    }
  })();
}

const noop = () => {};
const INVALID_PATHS = [undefined, null, 0].map((v) => `/${v}`);

// This is the external function which will call the history API.
export async function setRoute(
  pathname: string,
  search: URLSearchParamsInit = "",
  state: RouteState = {},
  // The only reason to suppress the warning is if we expect that
  // a route transition can happen during another route transition.
  suppressWarning = false,
) {
  if (
    !pathname ||
    !pathname.startsWith("/") ||
    INVALID_PATHS.find((p) => pathname.endsWith(p))
  ) {
    throw new Error(`Invalid call to setRoute, got pathname "${pathname}"`);
  }

  if (!isTransitionAllowed) {
    devDebug("setRoute blocked", pathname);
    return;
  }
  if (pathname.includes("?")) {
    devError(
      `Illegal character "?" in pathname, move to search param`,
      pathname,
    );
    const queryRegExp = /\?(.*)$/;
    search = pathname.match(queryRegExp)?.[1];
    pathname = pathname.replace(queryRegExp, "");
  }
  if (!suppressWarning) checkStackTrace();
  await mainRefs.appInit;
  state.__history_uid ??= assignHistoryUid();

  // We will wanna differentiate user-initiated vs. programmatic route transition.
  state.isProgrammatic = true;

  pathname = rewritePathname(pathname);
  const queryAsString = (
    search instanceof URLSearchParams ? search : new URLSearchParams(search)
  ).toString();
  const query = queryAsString ? `?${queryAsString}` : "";
  const lPathname = globals.location?.pathname;
  const lSearch = globals.location?.search;
  const lState = globals.history?.state;
  if (
    !IS_NODE &&
    (pathname !== lPathname || lSearch !== query || !deepEqual(lState, state))
  ) {
    globals.history.pushState(
      state,
      "",
      `${isHashRouting ? "#" : ""}${pathname}${query}`,
    );
  }
  if (!router.isLoaded && !suppressWarning && !IS_NODE_BROWSERLESS) {
    devWarn(
      `A route transition has been initiated while the previous route was ` +
        `still loading. If this happened because a network request took too long, ` +
        `it is safe to ignore this warning. However, if this is 100% reproducible, ` +
        `this is indicative of an anti-pattern where React components change ` +
        `the route after fetching data, which is the wrong thing to do! The ` +
        `correct way is to set the route to fetch new data, instead of the reverse.`,
      { lPathname, pathname },
    );
  }
  return __setRoute(pathname, search, state);
}

function checkStackTrace() {
  if (!IS_DEV) return;
  const { stack } = new Error();
  if (/invokeGuardedCallback/.test(stack)) {
    throw new Error(
      "Refusing to change route. Links must be implemented using " +
        "`href`, not from a click handler.",
    );
  }
}

export async function updateRoute(
  pathname: string,
  search: URLSearchParamsInit = "",
  state: RouteState = {},
  suppressWarning = false,
) {
  // updateRoute shouldn't be used from user input events to overwrite
  // the current pathname, but is allowed for setting search params and state.
  if (!suppressWarning && arguments.length === 1) checkStackTrace();
  state.__history_uid ??= assignHistoryUid();

  if (state.isUpdate === undefined) state.isUpdate = true;

  if (!state.isRedirect) {
    await mainRefs.appInit;
  }

  pathname = rewritePathname(pathname);

  const query = `${search.toString() ? "?" : ""}${search}`;

  if (!IS_NODE) {
    globals.history.replaceState(
      state,
      "",
      `${isHashRouting ? "#" : ""}${pathname}${query}`,
    );
  }

  state.isManual = true;

  return __setRoute(pathname, search, state);
}

const specialRouteMapping = {
  // Default route should be zero length.
  "/.*/": "",
};

export function matchRoutes(pathname: string) {
  const catchAllList = mainRefs.routes.filter((route) =>
    Array.isArray(route?.[catchAllRoutes]),
  );
  const routeMatchMap = new WeakMap();
  const baseMatches = mainRefs.routes
    .map((route) => {
      if (!route) return false;
      const { path } = route;
      if (typeof path === "string") return path === pathname ? route : false;

      if (path instanceof RegExp) {
        const match = pathname.match(path);
        if (!match) return false;
        routeMatchMap.set(route, match);
        return route;
      }

      return false;
    })
    .filter(Boolean) as BaseRoute[];

  const matches = baseMatches.map((route) => {
    const match = routeMatchMap.get(route);
    return {
      ...route,
      // Omit the 0 index which is just the string itself.
      parameters: match
        ? match
            .slice(1)
            .filter((_) => _ && !_.startsWith("/"))
            .map((_) => decodeURIComponent(_))
        : [],
    } as ActiveRoute;
  }) as ActiveRoute[];

  for (const route of baseMatches) {
    for (const catchAllRoute of catchAllList) {
      if ((catchAllRoute[catchAllRoutes] as BaseRoute[]).includes(route))
        matches.push(catchAllRoute as ActiveRoute);
    }
  }

  // Sort by longest matched route (more specific) first.
  matches.sort((a, b) => {
    let a1 = a.path?.toString() ?? "";
    let b1 = b.path?.toString() ?? "";
    if (specialRouteMapping.hasOwnProperty(a1)) a1 = specialRouteMapping[a1];
    if (specialRouteMapping.hasOwnProperty(b1)) b1 = specialRouteMapping[b1];

    let aPriority = a1.length;
    let bPriority = b1.length;

    // Catch-all routes should always be last. Priority given to `true`.
    if (a[catchAllRoutes]) aPriority -= 1000;
    if (b[catchAllRoutes]) bPriority -= 1000;
    if (a[catchAllRoutes] === true) aPriority -= 1;
    if (b[catchAllRoutes] === true) bPriority -= 1;

    return bPriority - aPriority;
  });

  return matches;
}

function setMissingComponent() {
  router.RouteComponent = () => MissingComponent;
  emitEvent(EVENT_CHANGE_ROUTE_COMPONENT, router.RouteComponent);
  emitEvent(EVENT_CHANGE_ROUTE, router.route);
}

let countKeys = 0;
let seenKeys = {};

// We need to dynamically resize the MAX_LENGTH based on the number of keys populated
// from a single route. This prevents accidental errors from occuring when keys
// get deleted unexpectedly.
stateEmitter.on(EVENT_ADD_KEY, (key) => {
  if (seenKeys[key]) return;
  seenKeys[key] = true;
  countKeys++;
  if (countKeys > MAX_LENGTH) {
    CONFIG.MAX_LENGTH = countKeys;
  }
});

let routeIdempotencyKey = 0;

async function __setRoute(
  pathname: string,
  search: URLSearchParamsInit = "",
  state: RouteState = {},
) {
  routeIdempotencyKey++;

  const idempotencyKey = routeIdempotencyKey;

  state.__history_uid ??= assignHistoryUid();
  const t0 = Date.now();
  pathname = normalizePathname(pathname);

  const matches = matchRoutes(pathname);

  const fetchDataFunctions = matches.map((route) => route.fetchData || noop);

  // Less specific to more specific order.
  fetchDataFunctions.reverse();

  // `decodeURIComponent` is needed here because there is a platform difference
  // between Electron and browsers. Electron seems to call this internally while
  // Node.js and browsers do not.
  const getPathname = () =>
    !IS_NODE
      ? normalizePathname(decodeURIComponent(globals.location.pathname))
      : pathname;

  const prevPathname = getPathname();
  router.lastPathname = prevPathname;
  const searchParams =
    search instanceof URLSearchParams ? search : new URLSearchParams(search);

  const route = matches[0];
  // Handle redirect route.
  if (route.redirect) {
    route.currentPath = prevPathname;

    const redirectPath =
      typeof route.redirect === "function"
        ? await route.redirect(route, searchParams, state)
        : route.redirect;

    // Premature exit just to avoid default handling of routing behavior.
    if (redirectPath === undefined) return;

    if (redirectPath !== false) {
      if (IS_NODE) throw new RedirectError(redirectPath);

      // This is used to whitelist redirects.
      state.isRedirect = true;

      state.isUpdate = false;

      if (typeof redirectPath === "string") {
        // If the redirect path is an external website that is not origin, browser security will prevent history state object from updating
        if (!IS_APP && !redirectPath.startsWith(globals.location.origin)) {
          globals.location.assign(redirectPath);
          return;
        }
        const searchRegExp = /\?(.+)$/;
        const cleanRedirectRoute = redirectPath.replace(searchRegExp, "");
        const redirectSearch = redirectPath.match(searchRegExp)?.[1];
        const redirectSearchParams = new URLSearchParams(redirectSearch);
        return updateRoute(cleanRedirectRoute, redirectSearchParams, state);
      }
    }
  }

  const initiatedBy = (route.initiatedBy =
    state.isManual || state.isInitializing
      ? ROUTE_INITIATED_BY_USER
      : ROUTE_INITIATED_BY_AUTO);

  // Handle 404 by showing 404 component and bailing out.
  if (!route || route[catchAllRoutes]) {
    const route = {
      path: pathname,
      currentPath: decodeURIComponent(pathname),
      initiatedBy: ROUTE_INITIATED_BY_USER,
      state,
      searchParams,
      parameters: [],
    };
    router.route = route;
    router.meta = missingMeta.bind(null, route.parameters, searchParams);
    extractMeta();

    setMissingComponent();

    const { referrer } = globals.document ?? {};
    const error = new NotFoundError(
      `Route not found! "${pathname}"${
        referrer ? `. Referrer: "${referrer}"` : ""
      }`,
    );
    throw error;
  }

  route.currentPath = decodeURIComponent(pathname);
  route.state = state;
  // Attach search params to current route.
  route.searchParams = searchParams;

  let resolver;
  __routeTransitions.push(
    new Promise((resolve) => {
      resolver = resolve;
    }),
  );
  const routeTransitionsIndex = __routeTransitions.length - 1;

  // Start loading data, don't have to wait for the module :)
  router.isLoaded = false;

  // Execute fetch functions serially.
  let dataError = null;
  const fetchAllData = () =>
    fetchDataFunctions
      .reduce(async (chain, fn) => {
        await chain;

        try {
          await fn.call(route, route.parameters, searchParams, state);
        } catch (error) {
          if (error instanceof NotFoundError) {
            setMissingComponent();
          }

          // If initially fails to load data, we still want to render the page.
          if (router.RouteComponent === INITIAL_ROUTE_COMPONENT) {
            if (error instanceof Error) {
              router.error = error;
            }

            devError("INTIAL FETCH FAILED", error);

            return;
          }

          devError("ROUTE FETCH FAILED", error);

          throw error;
        }
      }, Promise.resolve())
      .catch((error) => {
        if (
          !IS_NODE &&
          !IS_APP &&
          initiatedBy === ROUTE_INITIATED_BY_USER &&
          error instanceof ModuleImportError
        ) {
          devWarn("ROUTE FETCH FAILED", error);

          globals.location.href = route.currentPath;
        } else {
          devError("ROUTE FETCH FAILED", error);
        }

        dataError = error;
      });

  // This can be used to re-fetch data for the current route.
  router.refetchData = function () {
    devDebug("refetch called");
    fetchAllData();
  };

  if (router.route && router.route?.currentPath !== route.currentPath) {
    countKeys = 0;
    seenKeys = {};
    CONFIG.MAX_LENGTH = MAX_LENGTH;
  }

  const dataPromise = fetchAllData();

  emitEvent(EVENT_LOADING_DATA, route);
  const initialPathsWritten = recentlyPersistedPaths.length;

  let DefaultComponent = null;
  let meta = null;
  const firstRouteWithComponent = matches.find((_) => {
    return _.component;
  });
  if (firstRouteWithComponent) {
    let mod: ComponentModule;
    const { component } = firstRouteWithComponent;

    try {
      switch (typeof component) {
        case "string": {
          mod = componentCache.get(component);

          if (!mod) {
            const modulePath = getModulePath(component);

            // Hook for fetch is called here so that we can show loading state for
            // importing route component.
            hookFetch(modulePath);

            mod = await dynamicImport(modulePath);

            componentCache.set(component, mod);
          }
          break;
        }

        case "object": {
          mod = component;
          break;
        }

        case "function": {
          mod = await Promise.resolve(component());
          break;
        }

        default:
          throw new Error(`Invalid component definition: ${component}`);
      }
    } catch (err) {
      // Make sure we cleanup first.
      __cleanupRouteTransition(resolver, routeTransitionsIndex);

      if (
        !IS_NODE &&
        !IS_APP &&
        initiatedBy === ROUTE_INITIATED_BY_USER &&
        err instanceof ModuleImportError
      ) {
        devWarn("FAILED TO LOAD MODULE", err);

        globals.location.href = route.currentPath;
      } else {
        devError("FAILED TO LOAD MODULE", err);
      }

      throw err;
    }

    ({ default: DefaultComponent, meta } = mod);
  }

  if (!meta) {
    meta = defaultMeta;
    if (firstRouteWithComponent) {
      const name =
        typeof firstRouteWithComponent.component === "object"
          ? firstRouteWithComponent.component.default?.name
          : firstRouteWithComponent.component;
      devWarn(
        `Missing meta function for "${name}" component! ` +
          `Meta strings are important for search engine indexing and setting the globals title.`,
      );
    }
  }

  // ensure latest route before proceeding, because after this point, it sets
  // the actual route.
  const currPathname = getPathname();
  if (currPathname !== prevPathname || route.searchParams !== searchParams) {
    devWarn(
      `Route changed while loading module! (${Date.now() - t0}ms)
- Current  : ${currPathname}
- Previous : ${prevPathname}`,
    );

    // Take a snapshot of in-flight route transitions and wait.
    await Promise.all(Array.from(__routeTransitions));
  }

  router.previousRoute = router.route;
  router.route = route;

  const previousRouteComponent = router.RouteComponent;
  if (DefaultComponent) {
    if (!WrappedComponentMap.has(DefaultComponent)) {
      const WrappedComponent = () => DefaultComponent;
      for (const key of Object.keys(DefaultComponent)) {
        WrappedComponent[key] = DefaultComponent[key];
      }
      WrappedComponentMap.set(DefaultComponent, WrappedComponent);
    }
    router.RouteComponent = WrappedComponentMap.get(DefaultComponent);
  }

  // Curry the `meta` function to accept the same parameters as `fetchData`.
  router.meta = meta.bind(null, route.parameters, searchParams);

  extractMeta();

  emitEvent(EVENT_CHANGE_ROUTE, route);

  if (previousRouteComponent !== router.RouteComponent) {
    eventBus.once(EVENT_RENDER_ROUTE_COMPONENT, () => {
      devDebug(
        `rendered new route component for "${currPathname}" in ${
          Date.now() - t0
        }ms`,
      );
    });

    emitEvent(EVENT_CHANGE_ROUTE_COMPONENT, router.RouteComponent);
  }

  // This should run unconditionally, even if there was an error in
  // dataPromise or i18nInit, because we want to set the loading state
  // regardless of error.
  await Promise.all([dataPromise, i18nInit]);

  if (routeIdempotencyKey === idempotencyKey) {
    extractMeta();
  }

  pathsWritten.push([
    pathname,
    recentlyPersistedPaths.length - initialPathsWritten,
  ]);
  delete pathsWritten[pathsWritten.length - MAX_RECENT_PATHS - 1];

  router.error = dataError;
  router.isLoaded = true;
  emitEvent(EVENT_LOADED_DATA, route);
  __cleanupRouteTransition(resolver, routeTransitionsIndex);

  if (dataError) return Promise.reject(dataError);
}

function __cleanupRouteTransition(resolver, i) {
  resolver();
  delete __routeTransitions[i];

  // Check if we are allowed to reset index to zero.
  if (__routeTransitions.every((value) => !value)) {
    __routeTransitions.length = 0;
  }
}

const DEFAULT_META_IMAGE: MediaMeta = {
  url: hardCodeURLs.DEFAULT_OG_IMAGE,
  alt: ["common:blitzLogo", "Blitz Logo"],
  width: 640,
  height: 430,
};

mainRefs.DEFAULT_META_IMAGE = DEFAULT_META_IMAGE;

/**
 * This function extracts meta for the current route. The return value is used
 * only for SSR, otherwise it will set meta-info on the current document.
 */
export function extractMeta() {
  const metadata = (router.route.metadata = router.meta() ?? defaultMeta());

  emitEvent(EVENT_LOADED_META);

  const {
    title: rawTitle,
    description: rawDescription,
    image = mainRefs.DEFAULT_META_IMAGE,
    canonicalUrl: rawCanonicalUrl,
    schema,
    linkTags: metaLinkTags = [],
  } = metadata;

  // Enforce i18n
  const name = i18n.t(...mainRefs.DEFAULT_SITE);
  const title = i18n.t(...rawTitle) || rawTitle[1];
  const description = i18n.t(...rawDescription) || rawDescription[1];

  const canonicalUrl = rawCanonicalUrl ?? router.route.currentPath;

  const linkTags: LinkTag[] = [
    { rel: "canonical", href: canonicalUrl && appURLs.BLITZ + canonicalUrl },
    ...metaLinkTags,
  ];

  const metaTags: MetaTag[] = [
    { name: "description", content: description },
    { name: "og:type", content: "website" },
    { name: "og:site_name", content: name },
    { name: "og:title", content: title },
    { name: "og:description", content: description },
    { name: "og:locale", content: i18n.language },

    {
      name: "og:locale:alternate",
      content: LOCALES.map(([code]) => code).filter(
        (code) => code !== i18n.language,
      ),
    },

    // OpenGraph
    { name: "og:image", content: image.url },
    {
      name: "og:image:alt",
      content: image && i18n.t(...image.alt),
    },
    { name: "og:image:width", content: image.width },
    { name: "og:image:height", content: image.height },

    // Twitter
    {
      name: "twitter:card",
      content:
        image.url !== mainRefs.DEFAULT_META_IMAGE.url && "summary_large_image",
    },
    { name: "twitter:image", content: image.url },
  ];

  if (IS_NODE) {
    // SSR mode, building the string.
    // Be careful to escape strings in here.
    return [
      htmlEscape`<title>${title}</title>`,

      ...linkTags.map(createLinkTag),
      ...metaTags.map(createMetaTag),

      createSchemaTag(schema),
    ]
      .filter(Boolean)
      .join("\n");
  }

  // CSR mode, need to set the document title.
  // Please keep the title first and app name last.
  globals.document.title = IS_APP
    ? `${title} ⚡ ${i18n.t("common:blitz", "Blitz")}`
    : title;
}

type LinkTag = {
  rel: string;
  href?: string;
  as?: string;
};

function createLinkTag({ rel, href, as }: LinkTag) {
  if (!href) return null;

  if (as) {
    return htmlEscape`<link rel="${rel}" href="${href}" as="${as}" />`;
  }

  return htmlEscape`<link rel="${rel}" href="${href}" />`;
}

type MetaContent = string | number;

type MetaTag = {
  name?: string;
  property?: string;
  content: MetaContent | MetaContent[] | false | null | undefined;
} & (
  | { name: string; property?: undefined }
  | { name?: undefined; property: string }
);

function createMetaTag({ name, property, content }: MetaTag) {
  if (!content) return null;

  return (Array.isArray(content) ? content : [content])
    .map((content) => {
      if (name) {
        return htmlEscape`<meta name="${name}" content="${content}" />`;
      }

      return htmlEscape`<meta property="${property}" content="${content}">`;
    })
    .join("\n");
}

function prepareSchema(schema: Schema | Schema[]): object {
  if (Array.isArray(schema)) {
    return {
      "@context": "https://schema.org",
      "@graph": schema,
    };
  }

  return {
    "@context": "https://schema.org",
    ...schema,
  };
}

function createSchemaTag(schema?: Schema | Schema[]) {
  if (!schema) return null;

  return `<script type="application/ld+json">${JSON.stringify(
    prepareSchema(schema),
  )}</script>`;
}

export const initializeRoute = async (
  pathname?: string,
  search?: URLSearchParams,
  state?: RouteState,
) => {
  if (!state) state = {};
  state.__history_uid ??= assignHistoryUid(initialHistoryUid, true);
  state.isInitializing = true;

  await Promise.all(Object.values(featurePromises));
  if (IS_NODE_BROWSERLESS) return;
  await new Promise((resolve) => {
    // If there is no SSR'd page, then render immediately. If there is SSR content,
    // then render later. This is to prevent hydration from happening before
    // data has been loaded.
    const eventName = hasMarkup ? EVENT_LOADED_DATA : EVENT_CHANGE_ROUTE;
    router.events.once(eventName, () => {
      resolve(null);
    });

    const path = pathname ?? globals.location?.pathname;
    const params = search ?? new URLSearchParams(globals.location?.search);

    // We want to trigger a side effect (event) here.
    __setRoute(path, params, state).catch((error) => {
      devError("INITIAL SET ROUTE FAILED", error);
      router.error = error;
      router.events.emit(EVENT_LOADED_DATA);

      sourceMap(error)
        .then(([mappedOutput]) =>
          eventBus.emit(EVENT_ERROR, {
            error,
            mappedOutput,
            tags: ["initial_set_route"],
            route: router?.route?.path
              ? stringifyPath(router?.route?.path)
              : "",
            routeParams: router?.route?.parameters,
          }),
        )
        .catch((error) => {
          devError("FAILED TO SOURCEMAP", error);
        });
    });
  });

  // Special case: wait for defaultRoute to complete first.
  // This is because it does some app-initializing stuff in it,
  // like initializing settings and app shell.
  if (!hasMarkup && !router.error) {
    await mainRefs.initializeDefaultRoute;
  }
};

if (!IS_NODE) {
  scrollOnRouteChange(router, EVENT_CHANGE_ROUTE);
}

globals.__BLITZ_DEV__.router = router;
globals.__BLITZ_DEV__.setRoute = setRoute;

mainRefs.router = router;

export default router;
